// Robot material: the shared print toon (printMaterial, fogMax 0.35) patched with
//  - printed grunge (rust drips running down local -Y, chips, paper fibre) from one shared
//    procedural canvas texture sampled in object space, amount per vertex (aMat.z / aMat.w)
//  - a baked ink hull inside the same geometry (aMat.x = 1): drawn unlit in ink, so each
//    part is ONE draw with its outline instead of two
//  - per-vertex glow colour (aGlow) lit by a per-instance glow amount (telegraphs)
//  - per-instance status: emissive add + whiten (iFx), rim colour + glow amount (iRim), seed (iSeed)

import * as THREE from 'three';
import { printMaterial } from '../materials';
import { decalAtlas } from './decals';

let grunge: THREE.Texture | null = null;

/** 1024² RGBA data (drawn in 256-unit coordinates scaled by K, so streaks don't stair-step): R rust drips (bright at the seam, fading downward), G chips, B paper fibre. */
export function grungeTexture(): THREE.Texture {
  if (grunge) return grunge;
  const S = 1024, K = S / 256;
  const cv = document.createElement('canvas');
  cv.width = cv.height = S;
  const g = cv.getContext('2d')!;
  g.fillStyle = '#000';
  g.fillRect(0, 0, S, S);
  g.globalCompositeOperation = 'lighter';
  let seed = 7;
  const rnd = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
  // rust runs: each starts at a seam row and runs down (canvas +y = down the panel) as ONE
  // continuous tapered stroke at a constant strength, ending in a small bulb. The shader keeps
  // the runs whose strength clears its threshold, so a run shows whole or not at all (no dashes).
  // Drawn across the vertical wrap so the map tiles.
  const seams = [16 * K, 128 * K];
  for (let i = 0; i < 90; i++) {
    // clumps: runs gather under a few bolts/seam spots rather than a fence of stripes
    const clump = Math.floor(rnd() * 9);
    const x0 = ((clump * 29.7 + (rnd() - 0.5) * 30) * K + S) % S;
    const y0 = seams[i % seams.length] + (rnd() - 0.5) * 10 * K;
    const len = (40 + Math.pow(rnd(), 1.5) * 180) * K;
    const w = (1.2 + Math.pow(rnd(), 2) * 4) * K;
    const st = 0.3 + 0.7 * rnd();
    const L: number[][] = [], Rr: number[][] = [];
    let x = x0;
    const step = 3 * K;
    for (let k = 0; k <= len; k += step) {
      const f = 1 - k / len;
      x += (rnd() - 0.5) * 0.5 * K;
      const ww = w * (0.35 + 0.65 * Math.sqrt(f)) * 0.5;
      L.push([x - ww, k]); Rr.push([x + ww, k]);
    }
    for (const wrap of [0, -S, S]) {
      g.fillStyle = `rgba(255,0,0,${st.toFixed(3)})`;
      g.beginPath();
      L.forEach((p, n) => (n ? g.lineTo(p[0], y0 + p[1] + wrap) : g.moveTo(p[0], y0 + p[1] + wrap)));
      for (let n = Rr.length - 1; n >= 0; n--) g.lineTo(Rr[n][0], y0 + Rr[n][1] + wrap);
      g.closePath(); g.fill();
      g.beginPath(); g.arc(x, y0 + len + wrap, w * 0.45, 0, Math.PI * 2); g.fill();
      // the seam stain the run hangs from
      g.fillRect(x0 - w * 1.5, y0 - 1.5 * K + wrap, w * 3, 3 * K);
    }
  }
  // chips: small irregular flakes, denser in clumps
  for (let i = 0; i < 150; i++) {
    const cx = rnd() * S, cy = rnd() * S, r = (1 + Math.pow(rnd(), 2.2) * 7) * K;
    const a = 0.4 + rnd() * 0.6;
    g.fillStyle = `rgba(0,255,0,${a.toFixed(3)})`;
    g.beginPath();
    const n = 5 + Math.floor(rnd() * 3);
    for (let k = 0; k < n; k++) {
      const ang = (k / n) * Math.PI * 2, rr = r * (0.5 + rnd() * 0.7);
      const px = cx + Math.cos(ang) * rr, py = cy + Math.sin(ang) * rr * 0.8;
      if (k === 0) g.moveTo(px, py); else g.lineTo(px, py);
    }
    g.closePath(); g.fill();
  }
  // paper fibre / mottling in B
  const id = g.getImageData(0, 0, S, S);
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      const o = (y * S + x) * 4;
      const n = rnd() * 0.55 + 0.45 * (0.5 + 0.5 * Math.sin((x / K) * 0.11 + Math.sin((y / K) * 0.07) * 3.0));
      id.data[o + 2] = Math.floor(n * 255);
      id.data[o + 3] = 255;
    }
  }
  // A: smooth fbm value noise (tiling), the rust-patch field
  const vn = (oct: number) => {
    const n = 4 << oct, gr = new Float32Array(n * n);
    for (let i = 0; i < n * n; i++) gr[i] = rnd();
    return (x: number, y: number) => {
      const fx = (x / S) * n, fy = (y / S) * n;
      const x0 = Math.floor(fx), y0 = Math.floor(fy), tx = fx - x0, ty = fy - y0;
      const sx = tx * tx * (3 - 2 * tx), sy = ty * ty * (3 - 2 * ty);
      const at = (a: number, b: number) => gr[((b % n) * n) + (a % n)];
      const a0 = at(x0, y0) + (at(x0 + 1, y0) - at(x0, y0)) * sx;
      const a1 = at(x0, y0 + 1) + (at(x0 + 1, y0 + 1) - at(x0, y0 + 1)) * sx;
      return a0 + (a1 - a0) * sy;
    };
  };
  const o1 = vn(0), o2 = vn(1), o3 = vn(2), o4 = vn(3);
  const data = new Uint8Array(S * S * 4);
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      const o = (y * S + x) * 4;
      data[o] = id.data[o]; data[o + 1] = id.data[o + 1]; data[o + 2] = id.data[o + 2];
      const f = o1(x, y) * 0.5 + o2(x, y) * 0.27 + o3(x, y) * 0.15 + o4(x, y) * 0.08;
      data[o + 3] = Math.max(0, Math.min(255, Math.floor(((f - 0.5) * 1.7 + 0.5) * 255)));
    }
  }
  const t = new THREE.DataTexture(data, S, S, THREE.RGBAFormat);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.colorSpace = THREE.NoColorSpace;
  t.magFilter = THREE.LinearFilter;
  t.minFilter = THREE.LinearMipmapLinearFilter;
  t.generateMipmaps = true;
  t.anisotropy = 2;
  t.flipY = false;
  t.needsUpdate = true;
  grunge = t;
  return t;
}

const VERT_DECL = /* glsl */ `
attribute vec4 aMat;
attribute vec4 aGlow;
attribute vec4 iFx;
attribute vec4 iRim;
attribute float iSeed;
attribute vec2 aUv;
varying vec2 vDUv;
varying vec4 vMat;
varying vec3 vGlow;
varying vec4 vFx;
varying vec4 vRim;
varying vec3 vObjPos;
varying vec3 vObjN;
// scalars packed into one varying (SwiftShader-class GPUs run out of varying slots):
// x seed, y part glow, z height in the robot, w distance below the panel's top edge
varying vec4 vPack;
#define vSeed vPack.x
#define vPartGlow vPack.y
#define vDrip vPack.w
`;
const VERT_BODY = /* glsl */ `
vMat = aMat; vDUv = aUv; vDrip = aGlow.w; vGlow = aGlow.rgb; vFx = iFx; vRim = iRim; vSeed = iSeed;
vObjPos = position; vObjN = normal;
`;
const FRAG_DECL = /* glsl */ `
uniform sampler2D uGrunge;
uniform sampler2D uDecal;
varying vec2 vDUv;
uniform vec3 uRust;
uniform vec3 uRustDark;
varying vec4 vMat;
varying vec3 vGlow;
varying vec4 vFx;
varying vec4 vRim;
varying vec3 vObjPos;
varying vec3 vObjN;
varying vec4 vPack;
#define vSeed vPack.x
#define vPartGlow vPack.y
#define vDrip vPack.w
uniform float uH;
uniform vec3 uGrime;
uniform vec3 uMetalLight;
uniform vec3 uMetalDark;
`;
const FRAG_GRUNGE = /* glsl */ `
float rbMetal = 0.0;
{
  vec3 an = abs( vObjN );
  vec2 guv = an.y > 0.72 ? vObjPos.xz : vec2( an.x > an.z ? vObjPos.z : vObjPos.x, vObjPos.y );
  guv = guv * uGrungeScale + vec2( vSeed, vSeed * 1.37 );
  vec4 gr = texture2D( uGrunge, guv );
  float notInk = 1.0 - vMat.x;
  if ( vMat.y > 0.5 ) {
    vec4 dc = texture2D( uDecal, vDUv );
    if ( dc.a < 0.5 ) discard;
    diffuseColor.rgb = dc.rgb * vColor.rgb;
  }
  // each robot wears differently (a product line of junk)
  float cov = vMat.z * ( 0.72 + 0.56 * fract( vSeed * 3.17 ) );
  float lum = dot( diffuseColor.rgb, vec3( 0.299, 0.587, 0.114 ) );
  // height in the robot (0 feet .. 1 top): wear is layered from the ground up
  float h = clamp( vPack.z / uH, 0.0, 1.5 );
  float low = 1.0 - smoothstep( 0.0, 0.45, h );
  float side = step( an.y, 0.72 );
  // rust: soft orange blooms rising from the base and out of the seams (coverage), a darker core
  // where it is heaviest; softer still on top faces (no streaks or flecks up there)
  float field = gr.a + ( gr.b - 0.7 ) * 0.2;
  float bloom = ( cov * 0.7 + low * 0.5 * ( 0.55 + 0.9 * fract( vSeed * 5.3 ) ) ) * ( 0.55 + 0.45 * side );
  float pA = smoothstep( 1.06 - bloom * 0.8, 1.26 - bloom * 0.8, field + low * 0.55 * vMat.z ) * 0.9;
  float pB = smoothstep( 1.22 - bloom * 0.7, 1.4 - bloom * 0.7, field + low * 0.35 * vMat.z ) * 0.75;
  diffuseColor.rgb = mix( diffuseColor.rgb, uRust, pA * notInk );
  diffuseColor.rgb = mix( diffuseColor.rgb, uRustDark, pB * notInk );
  // rust runs bleeding down from every panel's top edge (seams, trims, rivet rows, the lid) and
  // from a second seam row lower down: whole continuous strokes of varied length from the run
  // map, rust or grime by column; side faces only
  if ( side > 0.5 ) {
    float u = guv.x;
    vec4 dg = texture2D( uGrunge, vec2( u * 1.13 + 0.31, 0.012 + vDrip * uGrungeScale * 0.9 ) );
    float dAmt = 0.25 + cov * 0.55 + fract( vSeed * 7.1 ) * 0.15;
    float th = 1.0 - dAmt * 0.7;
    float run = smoothstep( th, th + 0.06, dg.r ) * notInk;
    float grimeRun = step( 0.6, fract( floor( u * 7.0 ) * 0.618 + vSeed ) );
    vec3 runC = mix( mix( uRust, uRustDark, 0.55 ), uGrime * 0.85, grimeRun );
    diffuseColor.rgb = mix( diffuseColor.rgb, runC, run * 0.72 * uDrip );
  }
  // grime: dark olive-brown, heavier the nearer the ground
  float gk = 1.0 - smoothstep( 0.0, 0.38, h );
  float grime = gk * gk * notInk * ( 0.8 + 0.4 * gr.b );
  diffuseColor.rgb = mix( diffuseColor.rgb, diffuseColor.rgb * 0.32 + uGrime * 0.42, min( grime, 1.0 ) * 0.85 );
  // grass stains: green-brown smears kicked up on the lower skirt, patchy, sides only
  float skirt = ( 1.0 - smoothstep( 0.04, 0.2, h ) ) * side;
  float gs = smoothstep( 0.45, 0.62, gr.a * 0.7 + gr.b * 0.5 - h * 1.2 ) * skirt * notInk;
  diffuseColor.rgb = mix( diffuseColor.rgb, vec3( 0.2, 0.24, 0.08 ), gs * 0.7 );
  // chipped enamel ONLY on worn edges and corners (edge wear baked into aMat.w; flat faces and
  // curved shells carry none): a red-oxide primer ring with bare metal in the middle
  float cw = min( vMat.w, 1.6 ) * 0.42;
  float hasEdge = step( 0.02, cw ) * notInk;
  float primer = smoothstep( 1.0 - cw * 1.3, 1.03 - cw * 1.3, gr.g ) * hasEdge;
  float chip = smoothstep( 1.0 - cw, 1.03 - cw, gr.g ) * hasEdge;
  diffuseColor.rgb = mix( diffuseColor.rgb, vec3( 0.36, 0.13, 0.07 ), primer * 0.85 );
  diffuseColor.rgb = mix( diffuseColor.rgb, lum > 0.3 ? uMetalDark : uMetalLight, chip );
  /*PATINA*/
  rbMetal = chip;
  diffuseColor.rgb *= mix( 1.0, 0.93 + 0.12 * gr.b, notInk );
}
`;
// Guns only (opts.patina): years in a holster. The enamel is sun-faded and smoke-dulled (the bright
// creams pulled down toward bone and tobacco), oil and powder soot mottle it, fine scratches and
// bigger holster-worn edges go through to bright steel, and the dark unpainted parts read as blued
// steel, so the downlight in the saloon's gun case catches them. diffuseColor is linear here.
const FRAG_PATINA = /* glsl */ `
{
  vec3 dc0 = diffuseColor.rgb;
  float mx = max( max( dc0.r, dc0.g ), dc0.b );
  float rel = ( mx - min( min( dc0.r, dc0.g ), dc0.b ) ) / max( mx, 1e-3 );
  float l2 = dot( dc0, vec3( 0.299, 0.587, 0.114 ) );
  float steel = ( 1.0 - smoothstep( 0.3, 0.45, rel ) ) * ( 1.0 - smoothstep( 0.3, 0.55, l2 ) );
  // only the light enamel fades toward bone; dark wood and leather keep their colour and just darken
  vec3 aged = mix( dc0, vec3( l2 ) * vec3( 1.0, 0.95, 0.86 ), 0.5 * smoothstep( 0.12, 0.45, l2 ) );
  aged *= mix( 0.82, 0.46, smoothstep( 0.2, 0.7, l2 ) );
  aged = mix( aged, aged * vec3( 0.6, 0.67, 0.8 ), steel * 0.7 );
  // the concept sheets' cream bodies become parkerized gunmetal, mottled, so a gun reads as
  // a working tool; the accent enamels (teal, orange, plum) stay as worn paint over it
  float warm = step( dc0.b, dc0.g ) * step( dc0.g, dc0.r );
  float body = smoothstep( 0.22, 0.42, l2 ) * ( 1.0 - smoothstep( 0.5, 0.62, rel ) );
  aged = mix( aged, vec3( 0.022, 0.024, 0.028 ) * ( 0.7 + 0.6 * gr.b ) * ( 0.8 + 0.4 * gr.a ), body * 0.92 );
  // tan and walnut furniture: one deep oiled walnut
  float wood = warm * smoothstep( 0.55, 0.64, rel ) * ( 1.0 - smoothstep( 0.86, 0.94, rel ) );
  float grain = texture2D( uGrunge, guv * vec2( 0.35, 6.0 ) + vec2( 0.53, 0.2 ) ).b;
  aged = mix( aged, vec3( 0.07, 0.028, 0.014 ) * ( 0.55 + 0.9 * grain ), wood * 0.9 );
  float soot = smoothstep( 0.3, 0.8, 1.0 - gr.a ) * ( 0.45 + 0.55 * gr.b );
  aged = mix( aged, aged * vec3( 0.45, 0.4, 0.36 ), soot * 0.8 );
  diffuseColor.rgb = mix( dc0, aged, notInk * uPatina );
  float sc = texture2D( uGrunge, guv * vec2( 7.0, 0.45 ) + vec2( 0.37, 0.11 ) ).g;
  float scratch = smoothstep( 0.86, 0.92, sc ) * notInk * uPatina;
  float wear = smoothstep( 1.0 - cw * 1.7, 1.05 - cw * 1.7, gr.g ) * hasEdge * uPatina;
  float bare = max( scratch * 0.7, wear );
  diffuseColor.rgb = mix( diffuseColor.rgb, uMetalLight * 1.1, bare );
  chip = max( chip, max( bare, ( max( steel * 0.35, body * 0.2 ) ) * notInk * uPatina ) );
  // a gun's char amount is how far it sits out of the key light: it dims the paint, not the rim and gleam
  diffuseColor.rgb *= 1.0 - vRim.w;
}
`;
const FRAG_FX = /* glsl */ `
{
  // a negative whiten is a cloak (ROSTER.md, the Stalker): screen-door the body away by that much
  float cloak = max( 0.0, -vFx.w );
  if ( cloak > 0.0 && fract( sin( dot( floor( gl_FragCoord.xy ), vec2( 12.9898, 78.233 ) ) ) * 43758.5453 ) < cloak ) discard;
  float ink = vMat.x;
  outgoingLight += vGlow * vPartGlow * 1.7;
  outgoingLight = mix( outgoingLight, vec3( 1.0, 0.96, 0.88 ), max( vFx.w, 0.0 ) * ( 1.0 - ink ) );
  outgoingLight = mix( outgoingLight, vColor.rgb, ink );
  // print value structure: cool the shadow side toward teal/plum, keep the lit side warm
  float lit = clamp( dot( outgoingLight, vec3( 0.333 ) ) / max( dot( diffuseColor.rgb, vec3( 0.333 ) ), 1e-3 ), 0.0, 1.5 );
  outgoingLight = mix( outgoingLight, outgoingLight * vec3( 0.8, 0.86, 1.0 ), smoothstep( 1.0, 0.45, lit ) * ( 1.0 - ink ) * 0.85 );
  // warm key on the lit side so a body spans dark / mid / light, not one mid band
  outgoingLight *= mix( vec3( 1.0 ), vec3( 1.12, 1.04, 0.9 ), smoothstep( 0.75, 1.05, lit ) * ( 1.0 - ink ) );
  // golden rim on the sun side (silhouettes separate from the haze), faint all round
  vec3 sunV = normalize( ( viewMatrix * vec4( uSunDir, 0.0 ) ).xyz );
  float rim = 1.0 - clamp( abs( dot( normal, normalize( vViewPosition ) ) ), 0.0, 1.0 );
  rim = rim * rim * ( 0.4 + 2.4 * max( dot( normal, sunV ), 0.0 ) );
  outgoingLight += vRim.rgb * rim * ( 1.0 - ink ) * ( 1.0 + rbMetal * 2.5 );
  // paint is matte (a broad faint sheen); bare metal is tight and bright
  vec3 hv = normalize( sunV + normalize( vViewPosition ) );
  float nh = max( dot( normal, hv ), 0.0 );
  outgoingLight += vec3( 1.0, 0.93, 0.8 ) * ( ( pow( nh, 14.0 ) * 1.6 + 0.12 ) * rbMetal + pow( nh, 4.0 ) * 0.035 * ( 1.0 - rbMetal ) ) * ( 1.0 - ink );
  outgoingLight = mix( outgoingLight, outgoingLight * vec3( 0.3, 0.24, 0.2 ), vRim.w * ( 1.0 - ink ) * ( 1.0 - step( 0.001, uPatina ) ) );
  outgoingLight += vFx.rgb * ( 1.0 + ink * 2.2 );
}
`;

let ramp: THREE.DataTexture | null = null;
/** soft print ramp: shadow side ~60% of lit, a short smooth terminator (refs shade softly) */
function softRamp() {
  if (ramp) return ramp;
  const v = [100, 110, 135, 190, 240, 255, 255, 255];
  const d = new Uint8Array(v.length * 4);
  v.forEach((x, i) => d.set([x, x, x, 255], i * 4));
  ramp = new THREE.DataTexture(d, v.length, 1, THREE.RGBAFormat);
  ramp.minFilter = ramp.magFilter = THREE.LinearFilter;
  ramp.generateMipmaps = false;
  ramp.needsUpdate = true;
  return ramp;
}

export const PART_DECL = /* glsl */ `
uniform highp sampler2D uParts;
attribute float aPart;
mat4 rbPartMatrix( out float glow ) {
  int c = int( aPart + 0.5 ) * 4;
  int r = gl_InstanceID;
  vec4 a = texelFetch( uParts, ivec2( c, r ), 0 );
  vec4 b = texelFetch( uParts, ivec2( c + 1, r ), 0 );
  vec4 d = texelFetch( uParts, ivec2( c + 2, r ), 0 );
  glow = texelFetch( uParts, ivec2( c + 3, r ), 0 ).x;
  return transpose( mat4( a, b, d, vec4( 0.0, 0.0, 0.0, 1.0 ) ) );
}
`;

/**
 * The material pair for one robot type (they differ only by the part texture): colour
 * (print toon + grunge + status) and a shadow-depth material running the same part fetch.
 */
export function robotMaterial(parts: THREE.DataTexture, height = 2, opts: {
  grungeScale?: number; yOff?: number; drip?: number; key?: string;
  /** guns: holster wear and aged enamel (FRAG_PATINA), 0..1 */
  patina?: number;
  /** the key light's direction for the rim and the gleam (default: the sun, HAZE.uSunDir) */
  sunDir?: { value: THREE.Vector3 };
} = {}): { color: THREE.Material; depth: THREE.Material } {
  const m = printMaterial({ fogMax: 0.35, bands: 2 }) as THREE.MeshToonMaterial;
  m.gradientMap = softRamp();
  const prev = m.onBeforeCompile;
  const tex = grungeTexture();
  m.onBeforeCompile = (shader, r) => {
    prev.call(m, shader, r);
    if (opts.sunDir) shader.uniforms.uSunDir = opts.sunDir;
    shader.uniforms.uPatina = { value: opts.patina ?? 0 };
    shader.uniforms.uParts = { value: parts };
    shader.uniforms.uGrunge = { value: tex };
    shader.uniforms.uDecal = { value: decalAtlas() };
    shader.uniforms.uGrungeScale = { value: opts.grungeScale ?? 0.42 };
    shader.uniforms.uYOff = { value: opts.yOff ?? 0 };
    shader.uniforms.uDrip = { value: opts.drip ?? 1 };
    shader.uniforms.uRust = { value: new THREE.Color(0xbc6a2c) };
    shader.uniforms.uRustDark = { value: new THREE.Color(0x6e3a22) };
    shader.uniforms.uGrime = { value: new THREE.Color(0x4a4128) };
    shader.uniforms.uMetalLight = { value: new THREE.Color(0xa8a296) };
    shader.uniforms.uMetalDark = { value: new THREE.Color(0x5e5a52) };
    shader.uniforms.uH = { value: height };
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nuniform float uYOff;\n' + PART_DECL + VERT_DECL)
      .replace('#include <beginnormal_vertex>', '#include <beginnormal_vertex>\nfloat rbGlow;\nmat4 rbM = rbPartMatrix( rbGlow );\nobjectNormal = mat3( rbM ) * objectNormal;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\n' + VERT_BODY + 'vPartGlow = rbGlow;\ntransformed = ( rbM * vec4( transformed, 1.0 ) ).xyz;\nvPack.z = transformed.y + uYOff;');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nuniform float uGrungeScale;\nuniform float uDrip;\nuniform float uPatina;\n' + FRAG_DECL)
      .replace('#include <color_fragment>', '#include <color_fragment>\n' + FRAG_GRUNGE.replace('/*PATINA*/', opts.patina ? FRAG_PATINA : ''))
      .replace('#include <opaque_fragment>', FRAG_FX + '\n#include <opaque_fragment>');
  };
  m.customProgramCacheKey = () => 'robotPrint_v7' + (opts.key ?? '') + (opts.patina ? '+patina' : '');

  const d = new THREE.MeshDepthMaterial({ depthPacking: THREE.RGBADepthPacking });
  d.onBeforeCompile = (shader) => {
    shader.uniforms.uParts = { value: parts };
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\n' + PART_DECL)
      .replace('#include <begin_vertex>', '#include <begin_vertex>\n{ float g; transformed = ( rbPartMatrix( g ) * vec4( transformed, 1.0 ) ).xyz; }');
  };
  d.customProgramCacheKey = () => 'robotDepth_v2';
  return { color: m, depth: d };
}
